import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownUp, Plus, Recycle, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import {
  DataTable,
  KpiCard,
  Panel,
  inr,
  num,
  type DataColumn,
} from "@/components/admin/analytics/AnalyticsKit";
import { FieldGroup, NotesField, NumberField, SelectField, TextField } from "@/components/admin/delivery/form-bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  createStockAdjustment,
  listStockItems,
  listStockMovements,
} from "@/lib/admin/inventory.functions";
import {
  MOVEMENT_REASONS,
  MOVEMENT_REASON_LABEL,
  OUTBOUND_REASONS,
  adjustmentSchema,
  type AdjustmentInput,
  type StockMovement,
} from "@/lib/admin/inventory.schemas";

export const Route = createFileRoute("/_admin/admin/stock-adjustments")({
  head: () => ({
    meta: [
      { title: "Stock Adjustments — Admin" },
      {
        name: "description",
        content:
          "Record consumption, waste, damage, expiry, corrections and transfers with a full audit trail.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: StockAdjustmentsPage,
});

function today(): string {
  const now = new Date();
  return new Date(now.getTime() + (330 + now.getTimezoneOffset()) * 60_000)
    .toISOString()
    .slice(0, 10);
}

function StockAdjustmentsPage() {
  const queryClient = useQueryClient();
  const itemsFn = useServerFn(listStockItems);
  const movesFn = useServerFn(listStockMovements);
  const adjustFn = useServerFn(createStockAdjustment);

  const items = useQuery({
    queryKey: ["admin", "inventory", "items"],
    queryFn: () => itemsFn(),
    staleTime: 30_000,
  });
  const movements = useQuery({
    queryKey: ["admin", "inventory", "movements", 30],
    queryFn: () => movesFn({ data: { days: 30 } as never }),
    staleTime: 15_000,
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<AdjustmentInput>({
    item_id: "",
    reason: "consumption",
    quantity: 1,
    movement_date: today(),
    reference: "",
    notes: "",
  });
  const [formError, setFormError] = useState("");

  const submit = () => {
    const signed = OUTBOUND_REASONS.includes(form.reason)
      ? -Math.abs(form.quantity)
      : form.quantity;
    const parsed = adjustmentSchema.safeParse({ ...form, quantity: signed });
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? "Check the form");
      return;
    }
    setFormError("");
    adjust.mutate(parsed.data);
  };

  const adjust = useMutation({
    mutationFn: (payload: AdjustmentInput) => adjustFn({ data: payload as never }),
    onSuccess: async (res) => {
      toast.success(`Stock updated — new balance ${res.balance}`);
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["admin", "inventory"] });
    },
    onError: () => toast.error("Couldn't record the adjustment."),
  });

  const rows = useMemo(
    () =>
      (movements.data ?? []).filter((m) =>
        ["waste", "damage", "expired", "stock_correction", "manual_adjustment", "transfer", "consumption"].includes(
          m.reason,
        ),
      ),
    [movements.data],
  );

  const kpi = useMemo(() => {
    const value = (reasons: string[]) =>
      rows.filter((r) => reasons.includes(r.reason)).reduce((s, r) => s + r.value_paise, 0);
    return {
      total: rows.length,
      consumption: value(["consumption"]),
      waste: value(["waste", "damage", "expired"]),
      corrections: rows.filter((r) =>
        ["stock_correction", "manual_adjustment"].includes(r.reason),
      ).length,
    };
  }, [rows]);

  const columns: Array<DataColumn<StockMovement>> = [
    {
      key: "date",
      header: "Date",
      render: (r) => r.movement_date,
      sortValue: (r) => r.movement_date,
      exportValue: (r) => r.movement_date,
    },
    {
      key: "item",
      header: "Ingredient",
      render: (r) => <span className="font-medium text-foreground">{r.item_name}</span>,
      sortValue: (r) => r.item_name,
      exportValue: (r) => r.item_name,
    },
    {
      key: "reason",
      header: "Reason",
      render: (r) => (
        <Badge variant={["waste", "damage", "expired"].includes(r.reason) ? "destructive" : "secondary"}>
          {MOVEMENT_REASON_LABEL[r.reason] ?? r.reason}
        </Badge>
      ),
      exportValue: (r) => MOVEMENT_REASON_LABEL[r.reason] ?? r.reason,
    },
    {
      key: "qty",
      header: "Movement",
      align: "right",
      render: (r) => (
        <span className={r.quantity < 0 ? "text-destructive" : "text-foreground"}>
          {r.quantity > 0 ? "+" : ""}
          {r.quantity} {r.unit}
        </span>
      ),
      sortValue: (r) => r.quantity,
      exportValue: (r) => r.quantity,
    },
    {
      key: "value",
      header: "Value",
      align: "right",
      render: (r) => inr(r.value_paise / 100),
      sortValue: (r) => r.value_paise,
      exportValue: (r) => r.value_paise / 100,
    },
    {
      key: "balance",
      header: "Balance",
      align: "right",
      render: (r) => `${r.balance_stock} ${r.unit}`,
      sortValue: (r) => r.balance_stock,
      exportValue: (r) => r.balance_stock,
    },
    {
      key: "ref",
      header: "Reference",
      render: (r) => <span className="font-mono text-[11px]">{r.reference || "—"}</span>,
      exportValue: (r) => r.reference,
    },
    {
      key: "by",
      header: "User",
      render: (r) => r.performed_by,
      exportValue: (r) => r.performed_by,
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Stock Adjustments"
        description="Book consumption, waste, damage, expiry, corrections and transfers — every entry is auditable."
      />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Adjustments (30 days)"
          value={num(kpi.total)}
          tone="info"
          icon={ArrowDownUp}
          loading={movements.isPending}
        />
        <KpiCard
          label="Consumption value"
          value={inr(kpi.consumption / 100)}
          tone="warn"
          icon={TrendingDown}
          loading={movements.isPending}
        />
        <KpiCard
          label="Waste value"
          value={inr(kpi.waste / 100)}
          tone="danger"
          icon={Recycle}
          loading={movements.isPending}
        />
        <KpiCard
          label="Corrections"
          value={num(kpi.corrections)}
          tone="purple"
          icon={TrendingUp}
          loading={movements.isPending}
        />
      </div>

      <Panel title="Recent adjustments" subtitle="Last 30 days">
        <div className="mb-3 flex justify-end">
          <Button size="sm" className="gap-1.5" onClick={() => setOpen(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New adjustment
          </Button>
        </div>
        <DataTable
          rows={rows}
          columns={columns}
          loading={movements.isPending}
          exportName="stock-adjustments"
          importModule="stock-movements"
          pageSize={15}
          minWidth={1080}
          searchOn={(r) => `${r.item_name} ${r.reason} ${r.reference} ${r.performed_by}`}
        />
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New stock adjustment</DialogTitle>
          </DialogHeader>
          <FieldGroup title="Adjustment details" cols={2}>
            <SelectField
              label="Ingredient"
              value={form.item_id}
              onChange={(v) => setForm({ ...form, item_id: v })}
              options={(items.data ?? []).map((i) => ({
                value: i.id,
                label: `${i.item_name} — ${i.current_stock} ${i.unit}`,
              }))}
            />
            <SelectField
              label="Reason"
              value={form.reason}
              onChange={(v) => setForm({ ...form, reason: v as AdjustmentInput["reason"] })}
              options={MOVEMENT_REASONS.map((r) => ({
                value: r,
                label: MOVEMENT_REASON_LABEL[r] ?? r,
              }))}
            />
            <NumberField
              label="Quantity"
              step={0.01}
              value={form.quantity}
              onChange={(v) => setForm({ ...form, quantity: v })}
              suffix={
                OUTBOUND_REASONS.includes(form.reason) ? "deducted from stock" : "added to stock"
              }
            />
            <TextField
              label="Date"
              type="date"
              value={form.movement_date}
              onChange={(v) => setForm({ ...form, movement_date: v })}
            />
            <TextField
              label="Reference"
              value={form.reference}
              onChange={(v) => setForm({ ...form, reference: v })}
              placeholder="Indent, PO or batch number"
            />
            <NotesField value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
          </FieldGroup>
          {formError ? <p className="text-xs text-destructive">{formError}</p> : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={adjust.isPending}>
              {adjust.isPending ? "Saving…" : "Record adjustment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
